import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Building2, Map, FileText, Users, Handshake, Wallet, Megaphone, HardHat, Shield, LogOut, Menu, ScrollText, ExternalLink, BadgeDollarSign, Truck } from "lucide-react";
import { useAuth } from "./AuthContext";
import { ROLES, label } from "@/lib/constants";

const NAV = [
  { to: "/admin", end: true, l: "Dashboard", i: LayoutDashboard, m: "dashboard" },
  { to: "/admin/propiedades", l: "Propiedades", i: Building2, m: "properties" },
  { to: "/admin/crm", l: "CRM / Leads", i: Users, m: "leads" },
  { to: "/admin/negociaciones", l: "Negociaciones", i: Handshake, m: "deals" },
  { to: "/admin/contabilidad", l: "Contabilidad", i: Wallet, m: "finance" },
  { to: "/admin/publicidad", l: "Publicidad", i: Megaphone, m: "ads" },
  { to: "/admin/ventas", l: "Venta de Propiedades", i: BadgeDollarSign, m: "sales" },
  { to: "/admin/equipos", l: "Equipos Pesados", i: Truck, m: "heavy" },
  { to: "/admin/construccion", l: "Construcción", i: HardHat, m: "construction" },
  { to: "/admin/zonas", l: "Zonas (CMS)", i: Map, m: "zones" },
  { to: "/admin/contenido", l: "Contenido del sitio", i: FileText, m: "content" },
  { to: "/admin/usuarios", l: "Usuarios y roles", i: Shield, m: "users" },
  { to: "/admin/auditoria", l: "Auditoría", i: ScrollText, m: "audit" },
];

export default function AdminLayout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { document.title = "Admin | SoloRentaPC"; }, [loc.pathname]);
  if (user === undefined) return <div className="p-10 text-slate-500">Cargando...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const current = [...NAV].sort((a, b) => b.to.length - a.to.length).find((n) => loc.pathname.startsWith(n.to));
  const allowed = !current || can(current.m);
  const side = (
    <nav className="flex flex-col gap-1 p-4">
      {NAV.filter((n) => can(n.m)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.m}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#FF3333] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
          <n.i size={17} />{n.l}
        </NavLink>
      ))}
    </nav>
  );
  return (
    <div className="min-h-screen flex bg-[#F4F5F8]" data-testid="admin-layout">
      <aside className={`print:hidden fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 bg-[#121316] overflow-y-auto transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-6 font-display text-xl font-extrabold text-white">Solo<span className="text-[#FF3333]">Renta</span>PC</div>
        {side}
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 min-w-0">
        <header className="print:hidden sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu /></button>
          <a href="/" target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ExternalLink size={14} />Ver sitio</a>
          <div className="flex items-center gap-4">
            <div className="text-right"><p className="text-sm font-semibold" data-testid="admin-user-email">{user.email}</p><p className="text-xs text-slate-500">{label(ROLES, user.role)}</p></div>
            <button onClick={logout} className="p-2 rounded-full hover:bg-slate-100" data-testid="admin-logout-button" title="Salir"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="p-4 sm:p-8">{allowed ? <Outlet /> : <p className="text-slate-500" data-testid="admin-no-permission">No tienes permiso para acceder a este módulo.</p>}</main>
      </div>
    </div>
  );
}
